import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Generic User interface - extend this in your app
 */
export interface BaseUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Generic Auth Service interface - implement this in your app
 */
export interface AuthService<TUser extends BaseUser = BaseUser> {
  /**
   * Get current user from storage/API
   */
  getCurrentUser: () => Promise<TUser | null>;
  
  /**
   * Login user
   */
  login: (email: string, password: string) => Promise<{ user: TUser; token?: string }>;
  
  /**
   * Logout user
   */
  logout: () => Promise<void>;
  
  /**
   * Refresh user session
   */
  refreshSession?: () => Promise<TUser | null>;
  
  /**
   * Optional: Switch account/company
   */
  switchAccount?: (accountId: string) => Promise<void>;
}

/**
 * Generic Auth Context State
 */
export interface AuthContextState<TUser extends BaseUser = BaseUser> {
  user: TUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  switchAccount?: (accountId: string) => Promise<void>;
}

/**
 * Create a typed auth context
 */
export function createAuthContext<TUser extends BaseUser = BaseUser>() {
  const AuthContext = createContext<AuthContextState<TUser> | undefined>(undefined);

  interface AuthProviderProps {
    children: ReactNode;
    authService: AuthService<TUser>;
    onAuthChange?: (user: TUser | null) => void;
    persistSession?: boolean;
    storageKey?: string;
  }

  function AuthProvider({
    children,
    authService,
    onAuthChange,
    persistSession = true,
    storageKey = 'auth-user',
  }: AuthProviderProps) {
    const [user, setUser] = useState<TUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Initialize auth state
    useEffect(() => {
      const initAuth = async () => {
        try {
          // Try to get user from storage first if persistence enabled
          if (persistSession) {
            const storedUser = localStorage.getItem(storageKey);
            if (storedUser) {
              setUser(JSON.parse(storedUser));
            }
          }

          // Then fetch current user from service
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          
          if (persistSession && currentUser) {
            localStorage.setItem(storageKey, JSON.stringify(currentUser));
          }
          
          onAuthChange?.(currentUser);
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          setUser(null);
          if (persistSession) {
            localStorage.removeItem(storageKey);
          }
        } finally {
          setIsLoading(false);
        }
      };

      initAuth();
    }, []);

    const login = async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const { user: loggedInUser } = await authService.login(email, password);
        setUser(loggedInUser);
        
        if (persistSession) {
          localStorage.setItem(storageKey, JSON.stringify(loggedInUser));
        }
        
        onAuthChange?.(loggedInUser);
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    const logout = async () => {
      setIsLoading(true);
      try {
        await authService.logout();
        setUser(null);
        
        if (persistSession) {
          localStorage.removeItem(storageKey);
        }
        
        onAuthChange?.(null);
      } catch (error) {
        console.error('Logout failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    const refreshSession = async () => {
      if (!authService.refreshSession) return;
      
      try {
        const refreshedUser = await authService.refreshSession();
        setUser(refreshedUser);
        
        if (persistSession && refreshedUser) {
          localStorage.setItem(storageKey, JSON.stringify(refreshedUser));
        }
        
        onAuthChange?.(refreshedUser);
      } catch (error) {
        console.error('Session refresh failed:', error);
        setUser(null);
        if (persistSession) {
          localStorage.removeItem(storageKey);
        }
      }
    };

    const switchAccount = async (accountId: string) => {
      if (!authService.switchAccount) {
        throw new Error('Account switching not supported');
      }
      
      setIsLoading(true);
      try {
        await authService.switchAccount(accountId);
        await refreshSession();
      } catch (error) {
        console.error('Account switch failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    const value: AuthContextState<TUser> = {
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshSession,
      ...(authService.switchAccount && { switchAccount }),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
      throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
  }

  return { AuthProvider, useAuth, AuthContext };
}

// Export a default instance for simple use cases
export const { AuthProvider, useAuth, AuthContext } = createAuthContext<BaseUser>();
